// src/components/ResultsPage.js

import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

const ResultsPage = () => {
  const navigate = useNavigate();

  const [backendUrl, setBackendUrl] = useState("");

  const [groups, setGroups] = useState([]);
  const [exams, setExams] = useState([]);

  const [examId, setExamId] = useState("");
  const [examGroupId, setExamGroupId] = useState("");
  const [examStatus, setExamStatus] = useState("All");

  const [examResults, setExamResults] = useState([]);

  const [keyword, setKeyword] = useState("");
  const [studentGroupId, setStudentGroupId] = useState("");
  const [studentResults, setStudentResults] = useState([]);

  // ============================================================
  // RESULT DISPLAY SETTINGS
  //
  // HIDDEN  = student cannot see result
  // SUMMARY = marks / percentage / status only
  // FULL    = complete result including question-wise details
  // ============================================================

  const [resultDisplayMode, setResultDisplayMode] = useState("HIDDEN");
  const [loadingResultMode, setLoadingResultMode] = useState(false);
  const [savingResultMode, setSavingResultMode] = useState(false);

  // ============================================================
  // LOAD BACKEND URL
  // ============================================================

  useEffect(() => {
    const script = document.createElement("script");

    script.src = "/config.js";

    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      }
    };

    script.onerror = () => {
      console.error("❌ Unable to load /config.js");
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // ============================================================
  // LOAD INITIAL DATA
  // ============================================================

  useEffect(() => {
    if (!backendUrl) return;

    loadGroups();
    loadExams();
  }, [backendUrl]);

  // ============================================================
  // LOAD GROUPS
  // ============================================================

  const loadGroups = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/exam-groups/list`
      );

      setGroups(res.data || []);

    } catch (err) {
      console.error(
        "❌ Error loading groups:",
        err
      );
    }
  };

  // ============================================================
  // LOAD EXAMS
  // ============================================================

  const loadExams = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/exams/list`
      );

      setExams(res.data || []);

    } catch (err) {
      console.error(
        "❌ Error loading exams:",
        err
      );
    }
  };

  // ============================================================
  // LOAD RESULT DISPLAY MODE
  //
  // IMPORTANT:
  // This runs whenever the selected exam changes.
  //
  // Therefore when Admin -> Instructor -> Admin navigates,
  // the setting is read again from SQL Server.
  // ============================================================

  const loadResultDisplayMode = async (selectedExamId) => {

    if (
      !selectedExamId ||
      !backendUrl
    ) {
      setResultDisplayMode("HIDDEN");
      return;
    }

    setLoadingResultMode(true);

    try {

      console.log(
        `➡ GET /results/exam/${selectedExamId}/result-visibility`
      );

      const res = await axios.get(
        `${backendUrl}/results/exam/${selectedExamId}/result-visibility`
      );

      console.log(
        "📘 Result visibility response:",
        res.data
      );

      const data = res.data || {};

      /*
       * Supports both:
       *
       * {
       *   success: true,
       *   examId: 1004,
       *   displayMode: "FULL",
       *   showResultToStudents: true
       * }
       *
       * and the older/simple response:
       *
       * {
       *   displayMode: "FULL",
       *   showResultToStudents: true
       * }
       */

      const mode = String(
        data.displayMode ||
        data.Result_Display_Mode ||
        "HIDDEN"
      )
        .trim()
        .toUpperCase();

      if (
        mode === "HIDDEN" ||
        mode === "SUMMARY" ||
        mode === "FULL"
      ) {
        setResultDisplayMode(mode);

        console.log(
          `✅ Result display mode loaded: Exam ${selectedExamId} -> ${mode}`
        );

      } else {

        console.warn(
          "⚠️ Invalid display mode received:",
          mode
        );

        setResultDisplayMode("HIDDEN");
      }

    } catch (err) {

      console.error(
        "❌ Error loading result display mode:",
        err
      );

      /*
       * Do NOT retain an old exam's setting.
       * If the database cannot be read, use HIDDEN
       * as the safe default.
       */

      setResultDisplayMode("HIDDEN");

    } finally {

      setLoadingResultMode(false);

    }
  };

  // ============================================================
  // WHEN EXAM CHANGES
  //
  // Always retrieve the saved setting from database.
  // ============================================================

  useEffect(() => {

    if (!examId) {

      setResultDisplayMode("HIDDEN");

      return;
    }

    loadResultDisplayMode(examId);

  }, [examId, backendUrl]);

  // ============================================================
  // SAVE RESULT DISPLAY MODE
  //
  // This is the ONLY place where the dropdown changes
  // the database setting.
  // ============================================================

  const saveResultDisplayMode = async () => {

    if (!examId) {
      alert("Please select an exam first.");
      return;
    }

    const mode = String(
      resultDisplayMode || ""
    )
      .trim()
      .toUpperCase();

    const allowedModes = [
      "HIDDEN",
      "SUMMARY",
      "FULL"
    ];

    if (!allowedModes.includes(mode)) {
      alert(
        "Invalid result display mode."
      );
      return;
    }

    setSavingResultMode(true);

    try {

      console.log(
        `➡ PUT /results/exam/${examId}/result-visibility`
      );

      const res = await axios.put(
        `${backendUrl}/results/exam/${examId}/result-visibility`,
        {
          displayMode: mode
        }
      );

      console.log(
        "📘 Result visibility save response:",
        res.data
      );

      if (
        res.data?.success === false
      ) {
        throw new Error(
          res.data?.error ||
          "Unable to save result display mode."
        );
      }

      /*
       * IMPORTANT:
       * Use the value returned by the backend.
       * This confirms that the value saved in SQL Server
       * is also the value displayed on screen.
       */

      const savedMode = String(
        res.data?.displayMode ||
        mode
      )
        .trim()
        .toUpperCase();

      setResultDisplayMode(savedMode);

      console.log(
        `✅ Result display mode saved: Exam ${examId} -> ${savedMode}`
      );

      /*
       * Re-read from database immediately after saving.
       *
       * This is deliberate.
       * It verifies that the persisted database value is
       * exactly what the UI is displaying.
       */

      await loadResultDisplayMode(examId);

    } catch (err) {

      console.error(
        "❌ Error saving result display mode:",
        err
      );

      const message =
        err.response?.data?.error ||
        err.message ||
        "Unable to update result display mode.";

      alert(message);

      /*
       * If saving failed, re-read the actual database value
       * rather than leaving the UI showing an unsaved value.
       */

      await loadResultDisplayMode(examId);

    } finally {

      setSavingResultMode(false);

    }
  };

  // ============================================================
  // SEARCH EXAM WISE
  // ============================================================

  const searchExamWise = async () => {

    if (!examId) {
      alert("Please select an exam");
      return;
    }

    try {

      const res = await axios.get(
        `${backendUrl}/results/exam-wise`,
        {
          params: {
            examId,
            groupId: examGroupId || "",
            status: examStatus
          }
        }
      );

      setExamResults(
        Array.isArray(res.data)
          ? res.data
          : []
      );

    } catch (err) {

      console.error(
        "❌ Error fetching exam-wise results:",
        err
      );

      alert(
        "Server error while fetching exam wise results."
      );

      setExamResults([]);

    }
  };

  // ============================================================
  // SEARCH STUDENT WISE
  // ============================================================

  const searchStudentWise = async () => {

    if (!keyword) {
      alert("Enter student ID keyword");
      return;
    }

    try {

      const res = await axios.get(
        `${backendUrl}/results/student-wise`,
        {
          params: {
            keyword,
            groupId: studentGroupId || ""
          }
        }
      );

      setStudentResults(
        Array.isArray(res.data)
          ? res.data
          : []
      );

    } catch (err) {

      console.error(
        "❌ Error fetching student-wise results:",
        err
      );

      alert(
        "Server error while fetching student wise results."
      );

      setStudentResults([]);

    }
  };

  // ============================================================
  // EXCEL DOWNLOAD
  // ============================================================

 // ============================================================
// EXCEL DOWNLOAD
// ============================================================

const downloadExcel = (
  data,
  fileName
) => {

  if (
    !data ||
    data.length === 0
  ) {
    alert("No data to download");
    return;
  }

  const worksheetData = data.map((r) => ({

    "Student Name":
      r.Student_Name ||
      r.Name ||
      "",

    "Student ID":
      r.Student_ID ||
      "",

    "Group":
      r.Group_Name ||
      "",

    "Exam":
      r.Exam_Name ||
      "",

    "Attempt":
      r.Attempt_Number != null
        ? `Attempt ${r.Attempt_Number}`
        : "Not Attempted",

    "Score":
      r.Score != null
        ? r.Score
        : "",

    "Percentage":
      r.Percentage != null
        ? `${Number(
            r.Percentage
          ).toFixed(2)}%`
        : "",

    "Status":
      r.Result_Status ||
      "",

    "Best Attempt":
      r.Is_Best_Attempt
        ? "Yes"
        : "No",

    "Start Time":
      r.Start_Time
        ? new Date(
            r.Start_Time
          ).toLocaleString()
        : "",

    "End Time":
      r.End_Time
        ? new Date(
            r.End_Time
          ).toLocaleString()
        : ""

  }));


  const worksheet =
    XLSX.utils.json_to_sheet(
      worksheetData
    );


  const workbook =
    XLSX.utils.book_new();


  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Results"
  );


  const excelBuffer =
    XLSX.write(
      workbook,
      {
        bookType: "xlsx",
        type: "array"
      }
    );


  const blob =
    new Blob(
      [excelBuffer],
      {
        type:
          "application/octet-stream"
      }
    );


  saveAs(
    blob,
    `${fileName}.xlsx`
  );
};

  // ============================================================
  // TABLE RENDERER
  // ============================================================

  // ============================================================
// TABLE RENDERER
// ============================================================

const renderTable = (
  data,
  showDownload = false,
  fileName = "results"
) => {

  const rows = Array.isArray(data) ? data : [];

  return (
    <div
      style={{
        overflowX: "auto",
        marginTop: "20px"
      }}
    >

      <table style={tableStyle}>

        <thead style={tableHead}>
          <tr>

            <th>
              Student Name
            </th>

            <th>
              Group
            </th>

            <th>
              Exam
            </th>

            <th>
              Attempt
            </th>

            <th>
              Score
            </th>

            <th>
              Percentage
            </th>

            <th>
              Status
            </th>

            <th>
              Action
            </th>

          </tr>
        </thead>

        <tbody>

          {rows.length === 0 ? (

            <tr>
              <td
                colSpan="8"
                style={noDataText}
              >
                No results found.
              </td>
            </tr>

          ) : (

            rows.map((result, index) => (

              <tr
                key={
                  result.Attempt_ID ||
                  `${result.User_ID}-${result.Exam_ID}-${index}`
                }
              >

                {/* STUDENT */}

                <td>
                  {result.Student_Name ||
                   result.Name ||
                   "—"}
                </td>


                {/* GROUP */}

                <td>
                  {result.Group_Name || "—"}
                </td>


                {/* EXAM */}

                <td>
                  {result.Exam_Name || "—"}
                </td>


                {/* ATTEMPT */}

                <td>

                  {result.Attempt_Number != null ? (

                    <>
                      Attempt {result.Attempt_Number}

                      {result.Is_Best_Attempt && (
                        <span
                          title="Best Attempt"
                          style={{
                            marginLeft: "6px",
                            fontSize: "16px"
                          }}
                        >
                          ⭐
                        </span>
                      )}
                    </>

                  ) : (

                    "—"

                  )}

                </td>


                {/* SCORE */}

                <td>

                  {result.Score !== null &&
                   result.Score !== undefined
                    ? result.Score
                    : "—"}

                </td>


                {/* PERCENTAGE */}

                <td>

                  {result.Percentage !== null &&
                   result.Percentage !== undefined
                    ? `${Number(
                        result.Percentage
                      ).toFixed(2)}%`
                    : "—"}

                </td>


                {/* STATUS */}

                <td>

                  {result.Result_Status === "Passed" && (

                    <span
                      style={{
                        fontWeight: 600
                      }}
                    >
                      Passed
                    </span>

                  )}

                  {result.Result_Status === "Failed" && (

                    <span
                      style={{
                        fontWeight: 600
                      }}
                    >
                      Failed
                    </span>

                  )}

                  {result.Result_Status === "Not Attempted" && (

                    <span
                      style={{
                        fontWeight: 600
                      }}
                    >
                      Not Attempted
                    </span>

                  )}

                  {![
                    "Passed",
                    "Failed",
                    "Not Attempted"
                  ].includes(result.Result_Status) && (

                    <span>
                      {result.Result_Status || "—"}
                    </span>

                  )}

                </td>


                {/* ACTION */}

                <td>

                  {result.Attempt_ID ? (

                    <button
                      type="button"
                      style={detailsButton}
                      onClick={() =>
                        navigate(
                          `/results/attempt/${result.Attempt_ID}`
                        )
                      }
                    >
                      View Details
                    </button>

                  ) : (

                    "—"

                  )}

                </td>

              </tr>

            ))

          )}

        </tbody>

      </table>


      {/* EXCEL */}

      {showDownload &&
       rows.length > 0 && (

        <button
          style={{
            ...redBtn,
            marginTop: "15px"
          }}
          onClick={() =>
            downloadExcel(
              rows,
              fileName
            )
          }
        >
          Download Excel
        </button>

      )}

    </div>
  );
};
  // ============================================================
  // DISPLAY MODE DESCRIPTION
  // ============================================================

  const getResultModeDescription =
    () => {

      switch (
        resultDisplayMode
      ) {

        case "FULL":

          return (
            "Students can see marks, percentage and the complete question-wise result."
          );

        case "SUMMARY":

          return (
            "Students can see marks, percentage and status, but not the question-wise result."
          );

        case "HIDDEN":

        default:

          return (
            "Students will not be able to view their result."
          );
      }
    };

  // ============================================================
  // DISPLAY MODE LABEL
  // ============================================================

  const getResultModeLabel =
    () => {

      switch (
        resultDisplayMode
      ) {

        case "FULL":
          return "📋 Full Result";

        case "SUMMARY":
          return "📊 Summary Only";

        case "HIDDEN":
        default:
          return "🔒 Hide Result";
      }
    };

  // ============================================================
  // JSX
  // ============================================================

  return (

    <div style={pageContainer}>

      {/* ======================================================
          EXAM WISE RESULTS
          ====================================================== */}

      <h2 style={sectionTitle}>
        Exam Wise Results
      </h2>

      <div style={cardBox}>

        {/* ----------------------------------------------------
            FILTER ROW
            ---------------------------------------------------- */}

        <div style={rowStyle}>

          {/* GROUP */}

          <div style={colStyle}>

            <label style={labelStyle}>
              Group
            </label>

            <select
              value={examGroupId}
              onChange={(e) =>
                setExamGroupId(
                  e.target.value
                )
              }
              style={inputStyle}
            >

              <option value="">
                None selected
              </option>

              {groups.map(
                (g) => (

                  <option
                    key={g.Group_ID}
                    value={g.Group_ID}
                  >
                    {g.Group_Name}
                  </option>

                )
              )}

            </select>

          </div>

          {/* EXAM */}

          <div style={colStyle}>

            <label style={labelStyle}>
              Exam
            </label>

            <select
              value={examId}
              onChange={(e) => {

                const selectedId =
                  e.target.value;

                setExamId(
                  selectedId
                );

                /*
                 * Clear previous exam results.
                 * This prevents displaying results from the
                 * previous exam while the new exam is selected.
                 */

                setExamResults([]);

              }}
              style={inputStyle}
            >

              <option value="">
                Select Exam
              </option>

              {exams.map(
                (ex) => (

                  <option
                    key={ex.Exam_ID}
                    value={ex.Exam_ID}
                  >
                    {ex.Exam_Name}
                  </option>

                )
              )}

            </select>

          </div>

          {/* STATUS */}

          <div style={colStyle}>

            <label style={labelStyle}>
              Status
            </label>

            <select
              value={examStatus}
              onChange={(e) =>
                setExamStatus(
                  e.target.value
                )
              }
              style={inputStyle}
            >

              <option value="All">
                All
              </option>

              <option value="Passed">
                Passed
              </option>

              <option value="Failed">
                Failed
              </option>

              <option value="Not Attempted">
                Not Attempted
              </option>

            </select>

          </div>

          {/* SEARCH */}

          <div
            style={
              searchBtnWrapper
            }
          >

            <button
              style={redBtn}
              onClick={
                searchExamWise
              }
            >
              Search
            </button>

          </div>

        </div>

        {/* ==================================================
            STUDENT RESULT ACCESS
            ================================================== */}

        <div
          style={
            resultAccessBox
          }
        >

          <div
            style={
              resultAccessTitle
            }
          >
            🎓 Student Result Access
          </div>

          <div
            style={
              resultAccessSubtitle
            }
          >
            Control what students can see after completing this examination.
          </div>

          {/* MODE SELECTOR */}

          <div
            style={
              resultModeRow
            }
          >

            <select
              value={
                resultDisplayMode
              }
              disabled={
                !examId ||
                loadingResultMode ||
                savingResultMode
              }
              onChange={(e) =>
                setResultDisplayMode(
                  e.target.value
                )
              }
              style={
                resultModeSelect
              }
            >

              <option value="HIDDEN">
                🔒 Hide Result
              </option>

              <option value="SUMMARY">
                📊 Summary Only
              </option>

              <option value="FULL">
                📋 Full Result
              </option>

            </select>

            <button
              style={
                savingResultMode
                  ? disabledRedBtn
                  : redBtn
              }
              disabled={
                !examId ||
                loadingResultMode ||
                savingResultMode
              }
              onClick={
                saveResultDisplayMode
              }
            >

              {savingResultMode
                ? "Saving..."
                : "Apply"}

            </button>

          </div>

          {/* CURRENT SETTING */}

          <div
            style={
              currentSettingBox
            }
          >

            <strong>
              Current setting:
            </strong>{" "}

            {loadingResultMode
              ? "Loading..."
              : getResultModeLabel()}

            <div
              style={
                currentSettingDescription
              }
            >
              {examId
                ? getResultModeDescription()
                : "Select an exam to view its current result display setting."}
            </div>

          </div>

        </div>

        {/* EXAM RESULTS */}

        {renderTable(
          examResults,
          true,
          `Exam_${
            examId || "All"
          }_Results`
        )}

      </div>

      {/* ======================================================
          STUDENT WISE RESULTS
          ====================================================== */}

      <h2 style={sectionTitle}>
        Student Wise Results
      </h2>

      <div style={cardBox}>

        <div style={rowStyle}>

          {/* SEARCH STUDENT */}

          <div style={colStyle}>

            <label
              style={labelStyle}
            >
              Name / Enrollment Number
            </label>

            <input
              type="text"
              placeholder="Search student..."
              value={keyword}
              onChange={(e) =>
                setKeyword(
                  e.target.value
                )
              }
              style={inputStyle}
            />

          </div>

          {/* GROUP */}

          <div style={colStyle}>

            <label
              style={labelStyle}
            >
              Group
            </label>

            <select
              value={
                studentGroupId
              }
              onChange={(e) =>
                setStudentGroupId(
                  e.target.value
                )
              }
              style={inputStyle}
            >

              <option value="">
                None selected
              </option>

              {groups.map(
                (g) => (

                  <option
                    key={g.Group_ID}
                    value={g.Group_ID}
                  >
                    {g.Group_Name}
                  </option>

                )
              )}

            </select>

          </div>

          {/* SEARCH */}

          <div
            style={
              searchBtnWrapper
            }
          >

            <button
              style={redBtn}
              onClick={
                searchStudentWise
              }
            >
              Search
            </button>

          </div>

        </div>

        {renderTable(
          studentResults,
          true,
          "Student_Results"
        )}

      </div>

    </div>
  );
};

export default ResultsPage;


// ================================================================
// STYLES
// ================================================================

const pageContainer = {
  maxWidth: "1300px",
  margin: "20px auto",
  padding: "10px",
  fontFamily: "Inter, Arial"
};

const sectionTitle = {
  color: "#C70013",
  fontSize: "28px",
  fontWeight: 700,
  marginBottom: "18px"
};

const cardBox = {
  background: "#fff",
  borderRadius: "10px",
  padding: "22px",
  boxShadow:
    "0 4px 18px rgba(0,0,0,0.1)",
  marginBottom: "40px"
};

const rowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "20px"
};

const colStyle = {
  flex: "1 1 260px",
  minWidth: "240px"
};

const labelStyle = {
  fontSize: "14px",
  fontWeight: 600
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  marginTop: "6px",
  borderRadius: "6px",
  border:
    "1.5px solid #D0D0D0",
  fontSize: "15px",
  transition: "0.2s"
};

const searchBtnWrapper = {
  display: "flex",
  alignItems: "end"
};

const redBtn = {
  padding: "10px 20px",
  backgroundColor: "#C70013",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: 600,
  boxShadow:
    "0 3px 8px rgba(199,0,19,0.35)",
  transition: "0.3s"
};

const disabledRedBtn = {
  ...redBtn,
  opacity: 0.6,
  cursor: "not-allowed"
};


// ================================================================
// RESULT ACCESS STYLES
// ================================================================

const resultAccessBox = {
  marginTop: "22px",
  padding: "16px",
  border:
    "1px solid #d9d9d9",
  borderRadius: "8px",
  backgroundColor: "#fff"
};

const resultAccessTitle = {
  color: "#C70013",
  fontSize: "18px",
  fontWeight: 700,
  marginBottom: "5px"
};

const resultAccessSubtitle = {
  color: "#555",
  fontSize: "13px",
  marginBottom: "14px"
};

const resultModeRow = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap"
};

const resultModeSelect = {
  minWidth: "180px",
  padding: "9px 12px",
  borderRadius: "6px",
  border:
    "1.5px solid #C70013",
  fontSize: "14px",
  fontWeight: 600,
  backgroundColor: "#fff"
};

const currentSettingBox = {
  marginTop: "12px",
  padding: "11px",
  border:
    "1px solid #ddd",
  borderRadius: "6px",
  backgroundColor: "#fafafa",
  fontSize: "13px"
};

const currentSettingDescription = {
  marginTop: "3px",
  color: "#555"
};


// ================================================================
// TABLE STYLES
// ================================================================

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "800px",
  border:
    "1px solid #dcdcdc"
};

const tableHead = {
  background: "#EFEFEF",
  fontWeight: 700,
  position: "sticky",
  top: 0,
  borderBottom:
    "2px solid #ccc"
};

const noDataText = {
  padding: "18px",
  textAlign: "center",
  color: "#999",
  fontStyle:"italic"
};

const detailsButton = {
  backgroundColor: "#C70013",
  color: "#fff",
  border: "none",
  padding: "6px 12px",
  borderRadius: "5px",
  cursor: "pointer"
};